import type { JSONContent } from '@tiptap/core';
import { processNodeForCustomIcons, processNodeForMath } from '../processors';
import { getMarkdownManager } from './markdown-manager.util';
import { escapeBackslashesInMath, escapeTildesInMath } from './markdown.util';
import { applyNodeToTextProcessors } from './node-processors.util';

/**
 * Converts Tiptap inline content nodes (with marks) to a markdown string.
 * Wraps content in a paragraph and doc structure for MarkdownManager serialization.
 * Pre-processes inlineMath nodes back to $...$ text before serialization.
 */
export function serializeInlineContentToMarkdown(nodes: JSONContent[]): string {
  if (!nodes.length) {
    return '';
  }

  const manager = getMarkdownManager();
  const paragraph: JSONContent = { type: 'paragraph', content: nodes };
  const processed = applyNodeToTextProcessors(paragraph);
  const doc: JSONContent = {
    type: 'doc',
    content: [processed],
  };

  // Serialize and trim the result (removes trailing newlines from paragraph)
  return manager.serialize(doc).trim();
}

/**
 * Parses a markdown string into Tiptap inline content nodes.
 * Extracts the content from the first paragraph node.
 * Post-processes to convert $...$ LaTeX syntax to inlineMath nodes.
 */
export function parseMarkdownToInlineContent(markdown: string): JSONContent[] {
  if (!markdown) {
    return [{ type: 'text', text: '' }];
  }

  const manager = getMarkdownManager();
  const doc = manager.parse(escapeBackslashesInMath(escapeTildesInMath(markdown)));

  // Post-process each node to convert $...$, {{icon:...}}, etc. back to their respective nodes
  const processedContent = doc.content?.map((node) => processNodeForCustomIcons(processNodeForMath(node)));
  const inlineContent = processedContent?.flatMap((node) => node.content ?? []);
  return inlineContent?.length ? inlineContent : [{ type: 'text', text: markdown }];
}
